import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  Observable,
  catchError,
  finalize,
  map,
  of,
  tap,
  throwError,
} from 'rxjs';
import { AuthUtils } from '@/app/core/auth/auth.utils';
import { LocalStorage } from '@/app/core/local-storage';
import { UserService } from '@/app/core/user/user.service';
import { User } from '@/app/core/user/user.types';
import { environment } from '@/environments/environment';

type SignInCredentials = {
  email: string;
  password: string;
};

type SignInResponse = {
  success: boolean;
  accessToken: string;
  user: User;
};

type MeResponse = {
  success: boolean;
  data: User;
};

type MessageResponse = {
  success: boolean;
  message?: string;
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly baseUrl = environment.apiUrl + '/auth';

  private readonly _httpClient = inject(HttpClient);
  private readonly _userService = inject(UserService);
  private readonly _storage = inject(LocalStorage);

  private _authenticated = false;
  private _userId: number | null = null;

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  set accessToken(token: string) {
    this._storage.setItem('accessToken', token);
  }

  get accessToken(): string {
    return this._storage.getItem('accessToken') ?? '';
  }

  get userId(): number | null {
    return this._userId;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  forgotPassword(data: {
    email: string;
    role: string;
  }): Observable<MessageResponse> {
    return this._httpClient.post<MessageResponse>(
      `${this.baseUrl}/forgot-password`,
      data
    );
  }

  resetPassword(
    data: { password: string; token?: string },
    options?: { headers?: HttpHeaders }
  ): Observable<MessageResponse> {
    return this._httpClient.patch<MessageResponse>(
      `${this.baseUrl}/update-password`,
      data,
      options
    );
  }

  signIn(credentials: SignInCredentials): Observable<SignInResponse> {
    if (this._authenticated) {
      return throwError(() => new Error('User is already logged in.'));
    }

    return this._httpClient
      .post<SignInResponse>(`${this.baseUrl}/signin`, {
        ...credentials,
        role: 'admin',
      })
      .pipe(
        tap((response) => {
          this.accessToken = response.accessToken;
          this._authenticated = true;
          this._userService.user = response.user;
          this._userId = response.user.id;
        })
      );
  }

  /**
   * Restore the session from an existing access token by hydrating the user.
   */
  signInUsingToken(): Observable<boolean> {
    return this._httpClient.get<MeResponse>(`${this.baseUrl}/me`).pipe(
      map((response) => {
        this._authenticated = true;
        this._userService.user = response.data;
        this._userId = response.data.id;
        return true;
      }),
      catchError(() => {
        this._authenticated = false;
        return of(false);
      })
    );
  }

  signOut(): Observable<boolean> {
    return this._httpClient.get(`${this.baseUrl}/sign-out`).pipe(
      catchError(() => of(null)),
      finalize(() => {
        // Always clear local state, idempotent
        this._storage.removeItem('accessToken');
        this._authenticated = false;
        this._userId = null;
      }),
      map(() => true)
    );
  }

  check(): Observable<boolean> {
    // Already authenticated this session
    if (this._authenticated) {
      return of(true);
    }

    // No token → not authenticated
    if (!this.accessToken) {
      return of(false);
    }

    // Expired token → not authenticated
    if (AuthUtils.isTokenExpired(this.accessToken)) {
      return of(false);
    }

    // Valid token → restore the session
    return this.signInUsingToken();
  }

  /**
   * Whether a (non-expired) access token is present.
   */
  isAuthenticated(): boolean {
    return !!this.accessToken && !AuthUtils.isTokenExpired(this.accessToken);
  }

  sendOtp(email: string): Observable<MessageResponse> {
    return this._httpClient
      .post<MessageResponse>(`${this.baseUrl}/otp`, { email })
      .pipe(catchError((error) => this.handleError(error)));
  }

  verifyOtp(email: string, otp: string): Observable<MessageResponse> {
    return this._httpClient
      .post<MessageResponse>(`${this.baseUrl}/verify-otp`, { email, otp })
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * Clear all client-side auth state (cookies / storage).
   */
  clearAllAuthData(): void {
    this._storage.removeItem('accessToken');
    this._authenticated = false;
    this._userId = null;

    try {
      localStorage.clear();
      sessionStorage.clear();

      // Expire all cookies
      document.cookie.split(';').forEach((cookie) => {
        document.cookie = cookie
          .replace(/^ +/, '')
          .replace(/=.*/, '=;expires=' + new Date(0).toUTCString() + ';path=/');
      });
    } catch (error) {
      console.error('Failed to clear auth data:', error);
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  private handleError(error: unknown): Observable<never> {
    console.error('API Error:', error);
    const message = error instanceof Error ? error.message : 'Server Error';
    return throwError(() => new Error(message));
  }
}
